/**
 * "Which CV?" picker for the create flow (web twin of `CvPickerSheet.tsx`).
 *
 * Built on `WebDialog`, which is a centred dialog on desktop and a bottom sheet
 * under 768px — so this is the same decision surface the native sheet gives,
 * rather than a list unrolled under the option row where nothing tells the user
 * it is a distinct choice.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { WebDialog } from '../../common/WebDialog.web';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { formatRelativeDate } from '../../../lib/jobs-format';

export interface CvPickerItem {
  id: string;
  title: string;
  updatedAt: string;
  isMasterCv?: boolean;
  /** The user's main/default CV: pinned first and tagged "Default CV". */
  isMainCv?: boolean;
}

export interface CvPickerSheetProps {
  visible: boolean;
  onClose: () => void;
  cvs: CvPickerItem[];
  loading?: boolean;
  selectedCvId: string | null;
  onSelect: (cvId: string) => void;
}

export function CvPickerSheet({
  visible,
  onClose,
  cvs,
  loading,
  selectedCvId,
  onSelect,
}: CvPickerSheetProps) {
  const { t, i18n } = useTranslation('seeker');

  if (!visible) return null;

  return (
    <WebDialog
      visible={visible}
      title={t('cv.create.source.pickerTitle')}
      subtitle={t('cv.create.source.pickerSubtitle')}
      onClose={onClose}
      maxWidth={460}
    >
      {loading ? (
        <p style={EMPTY}>…</p>
      ) : cvs.length === 0 ? (
        <p style={EMPTY}>{t('cv.create.source.existingEmptyHint')}</p>
      ) : (
        <div style={LIST}>
          {cvs.map((cv) => {
            const active = cv.id === selectedCvId;
            const updated = cv.isMasterCv
              ? ''
              : formatRelativeDate(cv.updatedAt, i18n.language);
            return (
              <button
                key={cv.id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => {
                  onSelect(cv.id);
                  onClose();
                }}
                style={{
                  ...ROW,
                  backgroundColor: active ? 'rgba(0,0,0,0.06)' : 'transparent',
                  border: active
                    ? '1.5px solid var(--text-primary)'
                    : '1px solid var(--border-subtle, rgba(0,0,0,0.08))',
                }}
              >
                <span style={TITLE_ROW}>
                  <span style={TITLE} dir="auto">
                    {cv.title || t('cv.create.source.untitledCv')}
                  </span>
                  {cv.isMainCv ? (
                    <span className="tabbio-menu-badge" style={{ flexShrink: 0 }}>
                      {t('cv.create.source.defaultCvTag')}
                    </span>
                  ) : null}
                </span>
                {updated ? <span style={META}>{updated}</span> : null}
                {active ? (
                  <MaterialSymbol
                    name="check_circle"
                    size={20}
                    style={{ color: 'var(--text-primary)', flexShrink: 0 }}
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </WebDialog>
  );
}

const LIST: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: '12px 14px',
  borderRadius: 16,
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'start',
  transition: 'background-color 150ms, border-color 150ms',
};

const TITLE_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flex: 1,
  minWidth: 0,
};

const TITLE: CSSProperties = {
  fontSize: 15,
  fontWeight: 500,
  lineHeight: '20px',
  color: 'var(--text-primary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const META: CSSProperties = {
  flexShrink: 0,
  fontSize: 12.5,
  lineHeight: '17px',
  color: 'var(--text-tertiary)',
};

const EMPTY: CSSProperties = {
  fontSize: 14,
  lineHeight: '20px',
  color: 'var(--text-secondary)',
  textAlign: 'center',
  padding: '32px 0',
  margin: 0,
};
